import packageTerms from '../data/linkBuildingPackages.json' with { type: 'json' };
import { companyFacts } from '../data/companyFacts.mjs';

export const SITE_URL = 'https://linkzy.io';
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const LOGO_URL = `${SITE_URL}/favicon.png`;
// Only confirmed profiles belong here. Do not infer profiles from handles.
export const SAME_AS = ['https://www.linkedin.com/company/linkzyy'];

export const reference = (id) => ({ '@id': id });
export const pageUrl = (route = '/') => `${SITE_URL}${route === '/' ? '/' : `/${route.replace(/^\/+|\/+$/g, '')}`}`;
const entity = (route, fragment) => `${pageUrl(route)}#${fragment}`;

export function packageOfferNodes(route = '/') {
  return packageTerms.map(term => ({
    '@type': 'Offer', '@id': `${SITE_URL}/pricing#offer-${term.name.toLowerCase()}`,
    name: `${term.name} Package`, url: `${SITE_URL}/pricing`, price: term.price, priceCurrency: 'USD',
    seller: reference(ORGANIZATION_ID), itemOffered: reference(entity(route, 'service')),
    priceSpecification: { '@type': 'PriceSpecification', price: term.price, priceCurrency: 'USD' },
    description: `${term.links} niche-relevant guest posts with content included, DR ${term.minimumDR}+, ${term.monthlyTraffic}+ monthly organic traffic verified via Ahrefs. Custom report. Average delivery: ${term.deliveryWeeks.join(' to ')} weeks.`,
  }));
}

export function breadcrumbNode(route, title, items) {
  if (route === '/') return null;
  const parts = route.split('/').filter(Boolean);
  const parents = parts[0] === 'blog' && parts.length > 1 ? [{ name: 'Blog', url: `${SITE_URL}/blog` }]
    : parts.length > 1 && ['compare', 'industries'].includes(parts[0])
      ? [{ name: parts[0] === 'compare' ? 'Compare' : 'Industries', url: `${SITE_URL}/${parts[0]}` }] : [];
  return {
    '@type': 'BreadcrumbList', '@id': entity(route, 'breadcrumb'),
    itemListElement: (items || [{ name: 'Home', url: `${SITE_URL}/` }, ...parents, { name: title, url: pageUrl(route) }])
      .map((item, index) => ({ '@type': 'ListItem', position: index + 1, name: item.name, item: item.url === SITE_URL ? `${SITE_URL}/` : item.url })),
  };
}

export function articleNode({ route, title, description, image, author, datePublished, dateModified, ...extra }) {
  return {
    '@type': 'Article', '@id': entity(route, 'article'), url: pageUrl(route), headline: title, description,
    ...(image ? { image } : {}), ...(datePublished ? { datePublished } : {}),
    ...(dateModified || datePublished ? { dateModified: dateModified || datePublished } : {}),
    author: !author || author === 'Linkzy' ? reference(ORGANIZATION_ID) : { '@type': 'Person', name: author },
    publisher: reference(ORGANIZATION_ID), mainEntityOfPage: reference(entity(route, 'webpage')), ...extra,
  };
}

export function normalizeNode(node, route) {
  const { '@context': context, ...data } = node;
  const type = data['@type'];
  if (['Organization', 'WebSite', 'ProfessionalService'].includes(type)) return null;
  if (type === 'BreadcrumbList') return { ...data, '@id': entity(route, 'breadcrumb') };
  if (type === 'Article' || type === 'BlogPosting') return articleNode({
    route, title: data.headline, description: data.description, image: data.image,
    author: data.author?.name, datePublished: data.datePublished, dateModified: data.dateModified,
    ...(data.citation ? { citation: data.citation } : {}), ...(data.keywords ? { keywords: data.keywords } : {}),
  });
  if (type === 'Service') {
    const result = { ...data, '@id': entity(route, 'service'), provider: reference(ORGANIZATION_ID), mainEntityOfPage: reference(entity(route, 'webpage')) };
    if (route === '/' || route === '/pricing') {
      result.offers = { '@type': 'Offer', '@id': entity(route, 'starting-offer'), url: `${SITE_URL}/pricing`,
        priceSpecification: { '@type': 'PriceSpecification', minPrice: packageTerms[0].price, priceCurrency: 'USD' },
        seller: reference(ORGANIZATION_ID) };
      result.hasOfferCatalog = { '@type': 'OfferCatalog', name: 'Link Building Packages', itemListElement: packageOfferNodes().map(offer => reference(offer['@id'])) };
    }
    return result;
  }
  if (type === 'ItemList') return { ...data, '@id': data['@id'] || entity(route, 'itemlist'),
    itemListElement: data.itemListElement?.map(entry => ({ ...entry, item: entry.item?.['@type'] === 'Service'
      ? { ...entry.item, provider: reference(ORGANIZATION_ID) } : entry.item })) };
  return { ...data, '@id': data['@id'] || entity(route, String(type).toLowerCase()) };
}

export function createSchemaGraph({ route = '/', title = 'Linkzy', description = '', nodes = [] } = {}) {
  const servicePages = ['/', '/services', '/pricing'];
  const base = [
    { '@type': 'Organization', '@id': ORGANIZATION_ID, name: 'Linkzy', url: `${SITE_URL}/`,
      founder: { '@type': 'Person', name: companyFacts.founder },
      logo: { '@type': 'ImageObject', '@id': `${SITE_URL}/#logo`, url: LOGO_URL, contentUrl: LOGO_URL, width: 512, height: 512 }, sameAs: SAME_AS },
    { '@type': 'WebSite', '@id': WEBSITE_ID, name: 'Linkzy', url: `${SITE_URL}/`, publisher: reference(ORGANIZATION_ID), inLanguage: 'en' },
    { '@type': 'WebPage', '@id': entity(route, 'webpage'), url: pageUrl(route), name: title, description,
      isPartOf: reference(WEBSITE_ID), about: reference(ORGANIZATION_ID),
      ...(route !== '/' ? { breadcrumb: reference(entity(route, 'breadcrumb')) } : {}),
      ...(nodes.some(node => ['Article', 'BlogPosting'].includes(node['@type'])) ? { mainEntity: reference(entity(route, 'article')) }
        : servicePages.includes(route) ? { mainEntity: reference(entity(route, 'service')) } : {}) },
    breadcrumbNode(route, title),
    ...(servicePages.includes(route) ? [normalizeNode({ '@type': 'Service', name: 'Quality Backlinks Services', description: 'Niche-relevant guest post placements on real, traffic-verified websites. Content and reporting included.', serviceType: 'SEO Link Building', areaServed: 'Worldwide' }, route), ...packageOfferNodes(route)] : []),
  ];
  const unique = new Map();
  for (const node of [...base, ...nodes.map(node => normalizeNode(node, route))].filter(Boolean)) {
    unique.set(node['@id'], { ...unique.get(node['@id']), ...node });
  }
  return { '@context': 'https://schema.org', '@graph': [...unique.values()] };
}

export const serializeSchema = graph => JSON.stringify(graph).replace(/</g, '\\u003c');